"use client"

import { observer } from "mobx-react-lite"
import { useNavigate } from "react-router-dom"
import { useAccount, useConnect, useDisconnect } from "wagmi"

import { Button } from "@/components/button"
import { SheetClose, SheetHeader, SheetTitle } from "@/components/sheet"
import { useScaleApplication } from "@/domains/application/use-scale-application"
import { Lozenge } from "@/domains/common/components/lozenge"
import {
  Cancel1Icon,
  ChartIncreaseIcon,
  CloseIcon,
  IncentiveIcon,
  RewardsIcon,
  SettingsIcon,
  TickIcon,
  VoteIcon,
} from "@/domains/icons"
import { AnalyticsIcon } from "@/domains/icons/huge-icons/monotone/analytics"
import { FarmingIcon } from "@/domains/icons/huge-icons/monotone/farming"
import { LiquidityIcon } from "@/domains/icons/huge-icons/monotone/liquidity"
import { LockIcon } from "@/domains/icons/huge-icons/monotone/lock"
import { ManagerIcon } from "@/domains/icons/huge-icons/monotone/manager"
import { SwapIcon } from "@/domains/icons/huge-icons/monotone/swap"
import { WalletIcon } from "@/domains/icons/huge-icons/monotone/wallet"
import { cn } from "@/domains/library"
import { CL_PRODUCT_COPY } from "@/domains/manual-cl-page/cl-product-copy"
import { useWalletMenuStore } from "@/domains/wallet/use-wallet-menu-store"
import { getConnectorDisplayName, getConnectorIcon } from "@/domains/wallet/utils/connector-icon"
import useResponsive from "@/hooks/use-responsive"

type ConnectMenuContentProps = {
  onClose?: () => void
}

export const ConnectMenuContent = observer(({ onClose }: ConnectMenuContentProps) => {
  const scaleApp = useScaleApplication()
  const account = useAccount()
  const { disconnect } = useDisconnect()
  const { connect, connectors, isPending, error, reset } = useConnect()
  const navigate = useNavigate()
  const walletMenuStore = useWalletMenuStore()
  const { preferMobileNav } = useResponsive()

  const isConnected = account.isConnected && account.address
  const managerStore = scaleApp?.Manager.Store
  const managerAddresses = managerStore?.managerAddresses ?? []
  const selectedManagerAddress = managerStore?.selectedManagerAddress ?? null

  if (!walletMenuStore) {
    return null
  }

  const groupedConnectors = groupConnectors(connectors)

  const handleDisconnect = () => {
    disconnect()
  }

  const handleSelectManager = (address: (typeof managerAddresses)[0] | null) => {
    walletMenuStore.selectManager(address)
  }

  const handleSettingsClick = () => {
    if (onClose) {
      onClose()
    }
    navigate("/settings")
  }

  const handleNavigationClick = (path: string) => {
    if (onClose) {
      onClose()
    }
    navigate(path)
  }

  const handleConnectWallet = (connector: (typeof connectors)[0]) => {
    walletMenuStore.handleConnectClick()
    reset()
    const timeoutId = window.setTimeout(() => {
      reset()
    }, 30_000)
    connect(
      { connector },
      {
        onSettled: () => {
          window.clearTimeout(timeoutId)
        },
      },
    )
  }

  const isManagerApproved = (address: (typeof managerAddresses)[0]): boolean => {
    return managerStore?.getManagerApprovalStatus(address) ?? false
  }

  return (
    <div className="flex h-full flex-col">
      {/* Header */}
      <SheetHeader className="mb-6 flex-shrink-0">
        <div className="flex items-center justify-between">
          <SheetTitle className="text-xl font-semibold text-t100">Menu</SheetTitle>
          <SheetClose className="flex h-6 w-6 items-center justify-center rounded-sm bg-p200-shade text-t100/60 transition-all duration-300 ease-in-out hover:bg-p200-light hover:text-t100/80 disabled:pointer-events-none">
            <CloseIcon size={24} />
            <span className="sr-only">Close</span>
          </SheetClose>
        </div>
      </SheetHeader>

      {/* Content - Scrollable */}
      <div className="flex-1 space-y-6 overflow-y-auto">
        {/* Navigation Section */}
        <div className="space-y-3">
          <div className="text-sm font-medium text-t100">Navigation</div>
          <div className="space-y-1">
            <Button
              variant="mobile-nav-item"
              className="w-fit justify-start"
              onClick={() => handleNavigationClick("/")}
            >
              <SwapIcon className="h-5 w-5 flex-shrink-0" />
              <span>Home</span>
            </Button>
            <Button
              variant="mobile-nav-item"
              className="w-fit justify-start"
              onClick={() => handleNavigationClick("/dashboard")}
            >
              <RewardsIcon className="h-5 w-5 flex-shrink-0" />
              <span>Dashboard</span>
            </Button>
            <Button
              variant="mobile-nav-item"
              className="w-fit justify-start"
              onClick={() => handleNavigationClick("/swap")}
            >
              <SwapIcon className="h-5 w-5 flex-shrink-0" />
              <span>Swap</span>
            </Button>
            <Button
              variant="mobile-nav-item"
              className="w-fit justify-start"
              onClick={() => handleNavigationClick("/lock")}
            >
              <LockIcon className="h-5 w-5 flex-shrink-0" />
              <span>veSCALE</span>
            </Button>
            <Button
              variant="mobile-nav-item"
              className="w-fit justify-start"
              onClick={() => handleNavigationClick("/liquidity")}
            >
              <LiquidityIcon className="h-5 w-5 flex-shrink-0" />
              <span>Liquidity</span>
            </Button>
            <Button
              variant="mobile-nav-item"
              className={cn("w-fit justify-start", !preferMobileNav && "hidden")}
              onClick={() => handleNavigationClick("/liquidity/cl-farms")}
            >
              <FarmingIcon className="h-5 w-5 flex-shrink-0" />
              <span>CL Farms</span>
            </Button>
            <Button
              variant="mobile-nav-item"
              className={cn("w-fit justify-start", !preferMobileNav && "hidden")}
              onClick={() => handleNavigationClick("/liquidity/cl/v1")}
            >
              <ChartIncreaseIcon className="h-5 w-5 flex-shrink-0" />
              <span>{CL_PRODUCT_COPY.v1.shortName}</span>
            </Button>
            <Button
              variant="mobile-nav-item"
              className={cn("w-fit justify-start", !preferMobileNav && "hidden")}
              onClick={() => handleNavigationClick("/liquidity/cl/v2")}
            >
              <ChartIncreaseIcon className="h-5 w-5 flex-shrink-0" />
              <span>{CL_PRODUCT_COPY.v2.shortName}</span>
            </Button>
            <Button
              variant="mobile-nav-item"
              className="w-fit justify-start"
              onClick={() => handleNavigationClick("/vote")}
            >
              <VoteIcon className="h-5 w-5 flex-shrink-0" />
              <span>Vote</span>
            </Button>
            <Button
              variant="mobile-nav-item"
              className={cn("w-fit justify-start", !preferMobileNav && "hidden")}
              onClick={() => handleNavigationClick("/incentivize")}
            >
              <IncentiveIcon className="h-5 w-5 flex-shrink-0" />
              <span>Incentivize</span>
            </Button>
            <Button
              variant="mobile-nav-item"
              className="w-fit justify-start"
              onClick={() => handleNavigationClick("/analytics")}
            >
              <AnalyticsIcon className="h-5 w-5 flex-shrink-0" />
              <span>Analytics</span>
            </Button>
            <Button
              variant="mobile-nav-item"
              className="w-fit justify-start"
              onClick={() => handleNavigationClick("/settings")}
            >
              <SettingsIcon className="h-5 w-5 flex-shrink-0" />
              <span>Settings</span>
            </Button>
          </div>
        </div>

        {/* Wallet Section */}
        <div className="space-y-3">
          <div className="text-sm font-medium text-t100">Wallet</div>
          {!isConnected ? (
            <div className="space-y-3">
              {/* Recommended Wallets */}
              {groupedConnectors.recommended.length > 0 && (
                <div className="space-y-2">
                  <div className="text-xs text-t200">Recommended</div>
                  {groupedConnectors.recommended.map((connector: (typeof connectors)[0]) => {
                    const Icon = getConnectorIcon(connector)
                    return (
                      <Button
                        key={connector.id}
                        variant="disconnected"
                        className="w-fit justify-start"
                        onClick={() => handleConnectWallet(connector)}
                        disabled={isPending}
                      >
                        <Icon className="h-5 w-5 flex-shrink-0" />
                        {getConnectorDisplayName(connector)}
                      </Button>
                    )
                  })}
                </div>
              )}

              {/* Alternative Wallets */}
              {groupedConnectors.alternatives.length > 0 && (
                <div className="space-y-2">
                  <div className="text-xs text-t200">Alternatives</div>
                  {groupedConnectors.alternatives.map((connector: (typeof connectors)[0]) => {
                    const Icon = getConnectorIcon(connector)
                    return (
                      <Button
                        key={connector.id}
                        variant="disconnected"
                        className="w-fit justify-start"
                        onClick={() => handleConnectWallet(connector)}
                        disabled={isPending}
                      >
                        <Icon className="h-5 w-5 flex-shrink-0" />
                        {getConnectorDisplayName(connector)}
                      </Button>
                    )
                  })}
                </div>
              )}

              {/* Connection Error */}
              {error && (
                <div className="rounded-lg border border-error-border bg-error-background p-3 text-sm text-error-text">
                  {error.message || "Failed to connect wallet"}
                </div>
              )}

              {/* Connection Pending */}
              {isPending && <div className="text-center text-sm text-t200">Connecting...</div>}
            </div>
          ) : (
            <div
              className={cn(
                "relative flex cursor-pointer items-start justify-between rounded-lg border p-3 transition-colors",
                selectedManagerAddress === null
                  ? "border-connected-border bg-connected-background hover:brightness-[1.15]"
                  : "border-p400-light bg-p300 hover:brightness-[1.15]",
              )}
              onClick={() => handleSelectManager(null)}
            >
              <div className="flex min-w-0 flex-1 flex-col gap-1">
                <div className="flex items-center gap-2">
                  {(() => {
                    const IconComponent = walletMenuStore.currentIcon
                    return IconComponent ? (
                      <IconComponent className="h-4 w-4 flex-shrink-0 text-t100" />
                    ) : (
                      <WalletIcon className="h-4 w-4 flex-shrink-0 text-t100" />
                    )
                  })()}
                  <div className="truncate text-sm font-medium text-t100">
                    {walletMenuStore.currentConnectorName ? (
                      <>{walletMenuStore.currentConnectorName} connected wallet </>
                    ) : (
                      "Connected wallet"
                    )}
                  </div>
                </div>
                <div className="truncate font-mono text-xs text-t200">{account.address}</div>

                {selectedManagerAddress === null && (
                  <div
                    className="cursor-pointer p-0 text-xs text-t100 underline underline-offset-2"
                    onClick={(e: React.MouseEvent<HTMLDivElement>) => {
                      e.stopPropagation()
                      handleDisconnect()
                    }}
                  >
                    Disconnect
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Manager Section */}
        {managerAddresses.length > 0 && (
          <div className="space-y-3">
            <div className="text-sm font-medium text-t100">Managed Addresses</div>
            <div className="space-y-1">
              {/* Manager addresses */}
              {managerAddresses.map((address) => {
                const isApproved = isManagerApproved(address)
                const isSelected = selectedManagerAddress?.equals(address) ?? false
                const managerName = managerStore?.getManagerAddressName(address)

                return (
                  <div
                    key={address.toString()}
                    className={cn(
                      "flex cursor-pointer items-center justify-between rounded-lg border p-3 transition-colors",
                      isSelected
                        ? "border-manager-border bg-manager-background hover:brightness-[1.15]"
                        : "border-p400-light bg-p300 hover:brightness-[1.15]",
                    )}
                    onClick={() => handleSelectManager(address)}
                  >
                    <div className="flex min-w-0 flex-1 flex-col gap-1">
                      <div className="flex items-center gap-2">
                        <ManagerIcon className="h-4 w-4 flex-shrink-0 text-t100" />
                        <div className="truncate text-sm font-medium text-t100">{managerName || "Unnamed wallet"}</div>
                      </div>
                      <div className="truncate font-mono text-xs text-t200">{address.toString()}</div>
                      <div className="mt-1 flex items-center gap-2">
                        {isApproved ? (
                          <Lozenge color="success" size="xxs" contentLeft={<TickIcon size={12} />}>
                            Delegated
                          </Lozenge>
                        ) : (
                          <Lozenge color="error" size="xxs" contentLeft={<Cancel1Icon size={12} />}>
                            Disconnected
                          </Lozenge>
                        )}
                      </div>
                    </div>
                    {isSelected && <TickIcon className="ml-2 h-4 w-4 flex-shrink-0 text-t100" />}
                  </div>
                )
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  )
})

function groupConnectors<T extends { id: string; name: string }>(
  connectors: readonly T[],
): {
  recommended: T[]
  alternatives: T[]
} {
  const recommended: T[] = []
  const alternatives: T[] = []

  connectors.forEach((connector) => {
    const id = connector.id.toLowerCase()
    const name = connector.name.toLowerCase()

    if (id.includes("rabby") || name.includes("rabby") || id.includes("coinbase") || id.includes("walletconnect")) {
      recommended.push(connector)
    } else {
      alternatives.push(connector)
    }
  })

  return { recommended, alternatives }
}
